import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "../lib/api";
import { ALLERGEN_BY_CODE } from "../lib/format";
import { Sparkles, Flame, ChefHat, Leaf, Wine } from "lucide-react";

// Familles Google Fonts autorisées pour le menu (chargées à la volée
// selon le theme.fonts choisi par l'éditeur).
const MENU_FONTS = [
  "Playfair Display", "Inter", "Manrope", "DM Serif Display", "Merriweather",
  "Bricolage Grotesque", "Cormorant Garamond", "Space Grotesk", "Lora",
];
function loadMenuFonts(list = []) {
  if (typeof document === "undefined") return;
  const uniq = Array.from(new Set(list.filter((f) => f && MENU_FONTS.includes(f))));
  if (!uniq.length) return;
  const id = "menu-fonts-" + uniq.join("|").replace(/\s+/g, "+");
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.id = id;
  const fams = uniq.map((n) => `family=${n.replace(/\s+/g, "+")}:wght@400;600;700`).join("&");
  link.href = `https://fonts.googleapis.com/css2?${fams}&display=swap`;
  document.head.appendChild(link);
}

const LANGS = [
  { code: "fr", label: "Français", short: "FR", flag: "🇫🇷" },
  { code: "en", label: "English",  short: "EN", flag: "🇬🇧" },
  { code: "es", label: "Español",  short: "ES", flag: "🇪🇸" },
  { code: "it", label: "Italiano", short: "IT", flag: "🇮🇹" },
  { code: "de", label: "Deutsch",  short: "DE", flag: "🇩🇪" },
  { code: "zh", label: "中文",      short: "ZH", flag: "🇨🇳" },
  { code: "ja", label: "日本語",    short: "JA", flag: "🇯🇵" },
];

// Traduction des 9 catégories canoniques dans les 7 langues supportées.
// Utilisée à la fois par l'aperçu (iframe) et par la page publique QR.
// Sources : intitulés officiels/usuels. Reste local — pas d'appel réseau.
const CATEGORY_I18N = {
  "Viandes":              { fr: "Viandes",             en: "Meats",              es: "Carnes",           it: "Carni",              de: "Fleisch",             zh: "肉类",       ja: "肉料理" },
  "Produits de la mer":   { fr: "Produits de la mer",  en: "Seafood",            es: "Productos del mar", it: "Frutti di mare",    de: "Meeresfrüchte",       zh: "海鲜",       ja: "海の幸" },
  "Fruits et légumes":    { fr: "Fruits et légumes",   en: "Fruits & Vegetables", es: "Frutas y verduras", it: "Frutta e verdura",  de: "Obst & Gemüse",       zh: "水果与蔬菜", ja: "野菜と果物" },
  "Crèmerie":             { fr: "Crèmerie",            en: "Dairy",              es: "Lácteos",           it: "Latticini",          de: "Milchprodukte",       zh: "乳制品",     ja: "乳製品" },
  "Épicerie":             { fr: "Épicerie",            en: "Grocery",            es: "Ultramarinos",      it: "Drogheria",          de: "Lebensmittel",        zh: "杂货",       ja: "食料品" },
  "Vins":                 { fr: "Vins",                en: "Wines",              es: "Vinos",             it: "Vini",               de: "Weine",               zh: "葡萄酒",     ja: "ワイン" },
  "Spiritueux":           { fr: "Spiritueux",          en: "Spirits",            es: "Licores",           it: "Distillati",         de: "Spirituosen",         zh: "烈酒",       ja: "スピリッツ" },
  "Boissons":             { fr: "Boissons",            en: "Drinks",             es: "Bebidas",           it: "Bevande",            de: "Getränke",            zh: "饮品",       ja: "飲み物" },
  "Divers":               { fr: "Divers",              en: "Other",              es: "Varios",            it: "Vari",               de: "Sonstiges",           zh: "其他",       ja: "その他" },
  // Alias fréquents pour ne rien laisser en FR sur les cartes anciennes.
  "Entrée":               { fr: "Entrée",              en: "Starter",            es: "Entrante",          it: "Antipasto",          de: "Vorspeise",           zh: "开胃菜",     ja: "前菜" },
  "Entrées":              { fr: "Entrées",             en: "Starters",           es: "Entrantes",         it: "Antipasti",          de: "Vorspeisen",          zh: "开胃菜",     ja: "前菜" },
  "Plat":                 { fr: "Plat",                en: "Main",               es: "Plato",             it: "Piatto",             de: "Hauptgericht",        zh: "主菜",       ja: "メイン" },
  "Plats":                { fr: "Plats",               en: "Mains",              es: "Platos",            it: "Piatti",             de: "Hauptgerichte",       zh: "主菜",       ja: "メイン" },
  "Dessert":              { fr: "Dessert",             en: "Dessert",            es: "Postre",            it: "Dessert",            de: "Nachspeise",          zh: "甜点",       ja: "デザート" },
  "Desserts":             { fr: "Desserts",            en: "Desserts",           es: "Postres",           it: "Dessert",            de: "Nachspeisen",         zh: "甜点",       ja: "デザート" },
  "Autre":                { fr: "Autre",               en: "Other",              es: "Otros",             it: "Altro",              de: "Sonstiges",           zh: "其他",       ja: "その他" },
};
const catLabel = (cat, l) => CATEGORY_I18N[cat]?.[l] || cat;

// Labels des 14 allergènes réglementaires (INCO 1169/2011) — traduits par
// code (jamais par recette). Reste synchronisé avec ALLERGENS dans format.js.
const ALLERGEN_I18N = {
  GLU: { fr: "Gluten",         en: "Gluten",         es: "Gluten",           it: "Glutine",         de: "Gluten",           zh: "麸质",       ja: "グルテン" },
  CRU: { fr: "Crustacés",      en: "Crustaceans",    es: "Crustáceos",       it: "Crostacei",       de: "Krebstiere",       zh: "甲壳类",     ja: "甲殻類" },
  OEU: { fr: "Œufs",           en: "Eggs",           es: "Huevos",           it: "Uova",            de: "Eier",             zh: "蛋类",       ja: "卵" },
  POI: { fr: "Poissons",       en: "Fish",           es: "Pescado",          it: "Pesce",           de: "Fisch",            zh: "鱼类",       ja: "魚" },
  ARA: { fr: "Arachides",      en: "Peanuts",        es: "Cacahuetes",       it: "Arachidi",        de: "Erdnüsse",         zh: "花生",       ja: "落花生" },
  SOJ: { fr: "Soja",           en: "Soy",            es: "Soja",             it: "Soia",            de: "Soja",             zh: "大豆",       ja: "大豆" },
  LAI: { fr: "Lait",           en: "Milk",           es: "Leche",            it: "Latte",           de: "Milch",            zh: "乳制品",     ja: "乳" },
  FRU: { fr: "Fruits à coque", en: "Tree nuts",      es: "Frutos de cáscara", it: "Frutta a guscio", de: "Schalenfrüchte",   zh: "坚果",       ja: "ナッツ類" },
  CEL: { fr: "Céleri",         en: "Celery",         es: "Apio",             it: "Sedano",          de: "Sellerie",         zh: "芹菜",       ja: "セロリ" },
  MOU: { fr: "Moutarde",       en: "Mustard",        es: "Mostaza",          it: "Senape",          de: "Senf",             zh: "芥末",       ja: "マスタード" },
  SES: { fr: "Sésame",         en: "Sesame",         es: "Sésamo",           it: "Sesamo",          de: "Sesam",            zh: "芝麻",       ja: "ゴマ" },
  SUL: { fr: "Sulfites",       en: "Sulfites",       es: "Sulfitos",         it: "Solfiti",         de: "Sulfite",          zh: "亚硫酸盐",   ja: "亜硫酸塩" },
  LUP: { fr: "Lupin",          en: "Lupin",          es: "Altramuces",       it: "Lupino",          de: "Lupinen",          zh: "羽扇豆",     ja: "ルピナス" },
  MOL: { fr: "Mollusques",     en: "Molluscs",       es: "Moluscos",         it: "Molluschi",       de: "Weichtiere",       zh: "软体动物",   ja: "軟体動物" },
};
const allergenLabel = (code, l) => ALLERGEN_I18N[code]?.[l] || ALLERGEN_BY_CODE[code]?.label || code;
// Libellé de la section « Allergènes » traduit.
const ALLERGENS_SECTION_LABEL = {
  fr: "Allergènes · INCO 1169/2011",
  en: "Allergens · EU 1169/2011",
  es: "Alérgenos · UE 1169/2011",
  it: "Allergeni · UE 1169/2011",
  de: "Allergene · EU 1169/2011",
  zh: "过敏原 · 欧盟 1169/2011",
  ja: "アレルギー物質 · EU 1169/2011",
};
const MENU_LABEL = { fr: "Menu", en: "Menu", es: "Menú", it: "Menu", de: "Speisekarte", zh: "菜单", ja: "メニュー" };

const ICONS = { sparkles: Sparkles, flame: Flame, chef: ChefHat, leaf: Leaf, wine: Wine };

const ACCENT = {
  amber: { chip: "bg-amber-50 text-amber-900 ring-amber-200", dot: "bg-amber-500", band: "from-amber-50 to-white" },
  emerald: { chip: "bg-[#DCEBFF] text-[#0B1F33] ring-[#DCEBFF]", dot: "bg-[#2878FF]", band: "from-[#DCEBFF] to-white" },
  rose: { chip: "bg-rose-50 text-rose-900 ring-rose-200", dot: "bg-rose-500", band: "from-rose-50 to-white" },
  sky: { chip: "bg-sky-50 text-sky-900 ring-sky-200", dot: "bg-sky-500", band: "from-sky-50 to-white" },
  slate: { chip: "bg-slate-100 text-slate-900 ring-slate-200", dot: "bg-slate-800", band: "from-slate-50 to-white" },
};

const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(v || 0);

export default function PublicMenu() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [lang, setLang] = useState(() => {
    const nav = (typeof navigator !== "undefined" ? navigator.language || "" : "").slice(0, 2).toLowerCase();
    return LANGS.find((l) => l.code === nav)?.code || "fr";
  });
  const [err, setErr] = useState(null);
  // Live preview override — l'éditeur envoie un postMessage `rentascan:preview-theme`
  // à chaque modification de thème pour appliquer le nouveau look sans
  // recharger l'iframe. Ne persiste PAS côté serveur ; PublicMenu utilise
  // le thème du postMessage s'il est présent, sinon celui du payload.
  // Le postMessage transmet aussi `translationsAllowed` (basé sur le plan
  // effectif du restaurateur, simulateur inclus) afin que le sélecteur
  // de langues s'affiche dans l'Aperçu client sans passer par l'endpoint
  // public non authentifié.
  const [previewTheme, setPreviewTheme] = useState(null);
  const isPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";
  const [previewTranslationsAllowed, setPreviewTranslationsAllowed] = useState(null);
  useEffect(() => {
    if (!isPreview) return;
    const onMsg = (e) => {
      if (!e.data || e.data.type !== "rentascan:preview-theme") return;
      setPreviewTheme(e.data.theme || null);
      if (typeof e.data.translationsAllowed === "boolean") {
        setPreviewTranslationsAllowed(e.data.translationsAllowed);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [isPreview]);
  // En preview mode ouvert dans un nouvel onglet (pas d'iframe parent), on
  // interroge `/plan/me` pour connaître le plan effectif (simulateur inclus)
  // et débloquer le sélecteur de langues sans dépendre du postMessage.
  useEffect(() => {
    if (!isPreview || previewTranslationsAllowed !== null) return;
    fetch(`${API}/plan/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) return;
        const allowed = (p?.limits?.translations_allowed || 0) > 0;
        setPreviewTranslationsAllowed(allowed);
      })
      .catch(() => { /* silencieux — on retombe sur translations_visible */ });
  }, [isPreview, previewTranslationsAllowed]);

  useEffect(() => {
    fetch(`${API}/public/menu/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => setData(d))
      .catch(() => setErr("Menu introuvable"));
  }, [slug]);
  // NB : les traductions sont pré-générées par « Traduire le menu » (Intégral)
  // depuis l'éditeur. La page publique ne relance JAMAIS de traduction —
  // elle se contente d'afficher les langues déjà présentes dans le payload
  // et retombe sur le français si une langue manque.

  const nameForLang = (it, l) => {
    if (l === "fr") return it.display_name?.trim() || it.recipe?.name || "";
    return it.translations?.[l]?.display_name?.trim() || it.display_name?.trim() || it.recipe?.name || "";
  };
  const descForLang = (it, l) => {
    if (l === "fr") return it.description || it.recipe?.description || "";
    return it.translations?.[l]?.description || it.description || it.recipe?.description || "";
  };
  const headerForLang = (l) => {
    if (!data) return { title: "", subtitle: "", footer: "" };
    // Le titre du restaurant n'est JAMAIS traduit — toujours en français original
    const title = data.title || data.establishment_name || "";
    if (l === "fr") return { title, subtitle: data.subtitle, footer: data.footer };
    const t = data.translations?.[l] || {};
    return { title, subtitle: t.subtitle || data.subtitle, footer: t.footer || data.footer };
  };
  const items = useMemo(() => (data?.items || []).filter((it) => it.active && it.recipe), [data]);
  const byCategory = useMemo(() => items.reduce((acc, it) => {
    const cat = it.recipe.category || "Autre";
    (acc[cat] = acc[cat] || []).push(it);
    return acc;
  }, {}), [items]);

  // Applique le thème enregistré via variables CSS + charge les polices
  // (Google Fonts). translations_visible pilote l'affichage du sélecteur de
  // langues (rétrogradation Pro/Essentiel = masqué, données conservées).
  // ⚠ Ces hooks doivent rester AVANT tout early-return (rules-of-hooks).
  const themeStyle = useMemo(() => {
    const theme = previewTheme || data?.theme || {};
    const c = theme.colors || {};
    const f = theme.fonts || {};
    const bg = theme.background || {};
    return {
      "--rs-menu-bg": c.background || "#fafaf7",
      "--rs-menu-text": c.text_main || "#0f172a",
      "--rs-menu-text-secondary": c.text_secondary || "#5E6875",
      "--rs-menu-accent": c.accent || "#0f172a",
      "--rs-menu-titles": c.titles || "#0f172a",
      "--rs-menu-separators": c.separators || "#e2e8f0",
      "--rs-menu-font-titles": `"${f.titles || "Playfair Display"}", serif`,
      "--rs-menu-font-dish": `"${f.dish || "Inter"}", system-ui, sans-serif`,
      "--rs-menu-font-desc": `"${f.description || "Inter"}", system-ui, sans-serif`,
      "--rs-menu-font-price": `"${f.price || "Inter"}", system-ui, sans-serif`,
      color: "var(--rs-menu-text)",
      backgroundColor: bg.type === "solid" ? (bg.value || c.background || "#fafaf7") : (c.background || "#fafaf7"),
      backgroundImage: bg.type === "image" && bg.value ? `url(${bg.value})` : undefined,
      backgroundSize: bg.type === "image" ? "cover" : undefined,
      fontFamily: "var(--rs-menu-font-dish)",
    };
  }, [data?.theme, previewTheme]);
  useEffect(() => {
    const f = (previewTheme || data?.theme)?.fonts || {};
    loadMenuFonts([f.titles, f.dish, f.description, f.price]);
  }, [data?.theme, previewTheme]);

  // Force `fr` quand la formule n'autorise pas les traductions (rétrogradation
  // Pro/Essentiel) — même si le navigateur est en `en` ou autre.
  // NB : en mode preview (iframe éditeur), on suit `previewTranslationsAllowed`
  // transmis par postMessage afin que le simulateur Intégral fonctionne.
  useEffect(() => {
    if (!data) return;
    const allowed = isPreview && previewTranslationsAllowed !== null
      ? previewTranslationsAllowed
      : data.translations_visible !== false;
    if (!allowed && lang !== "fr") setLang("fr");
  }, [data, lang, isPreview, previewTranslationsAllowed]);

  const buildPhotoUrl = (u) => {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    if (u.startsWith("/")) return `${API.replace(/\/api$/, "")}${u}`;
    return u;
  };

  // Résout `show_photos` / `show_descriptions` depuis le thème (preview
  // ou payload). `true` par défaut si non défini pour compat rétro.
  const activeTheme = previewTheme || data?.theme || {};
  const showPhotos = activeTheme.show_photos !== false;
  const showDescriptions = activeTheme.show_descriptions !== false;

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">…</div>;

  const h = headerForLang(lang);
  const ds = data.daily_special || {};
  const dsActive = ds.active && (ds.title || (ds.translations?.[lang]?.title));
  const dsAcc = ACCENT[ds.accent] || ACCENT.amber;
  const DsIcon = ICONS[ds.icon] || Sparkles;
  const dsTitle = lang === "fr" ? ds.title : (ds.translations?.[lang]?.title || ds.title);
  const dsDesc = lang === "fr" ? ds.description : (ds.translations?.[lang]?.description || ds.description);
  const usedAllergens = Array.from(new Set(items.flatMap((it) => it.recipe?.allergens || [])));
  // En preview (iframe éditeur), on masque toujours le sélecteur de langues
  // — la barre pollue l'aperçu et n'a d'utilité que pour le client final.
  // Sur la vraie page publique (Aperçu client ou visiteur du QR code), le
  // sélecteur des 7 langues n'apparaît QUE si le propriétaire du menu est
  // sur la formule Intégral — c'est le backend qui règle `translations_visible`
  // via `get_effective_plan()` (§ menu.py:_menu_owner_plan_allows_translations).
  // Check strict `=== true` : si le backend renvoie undefined/null, on masque
  // par défaut, jamais l'inverse.
  const showLangSelector = !isPreview && data?.translations_visible === true;

  return (
    <div className="min-h-screen" style={themeStyle} data-testid="public-menu-page">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/70 border-b border-slate-200/60">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{MENU_LABEL[lang] || "Menu"}</div>
            <div className="font-semibold text-slate-900 truncate text-sm" data-testid="public-menu-title" style={{ fontFamily: "var(--rs-menu-font-titles)" }}>{h.title}</div>
          </div>
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="public-lang-switcher" style={{ display: showLangSelector ? undefined : "none" }}>
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`public-lang-${l.code}`}
                onClick={() => setLang(l.code)}
                title={l.label}
                className={`text-xs font-medium px-2.5 py-1.5 rounded-full transition-colors shrink-0 flex items-center gap-1 ${
                  lang === l.code ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <span aria-hidden>{l.flag}</span>
                <span className="hidden sm:inline">{l.label}</span>
                <span className="sm:hidden">{l.short}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-8 pb-16">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">{h.subtitle}</div>
          <h1
            className="mt-1 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "var(--rs-menu-font-titles)" }}
            data-testid="public-menu-h1"
          >
            {h.title}
          </h1>
        </div>

        {/* Ardoise du Jour */}
        {dsActive && (
          <div
            data-testid="daily-special-band"
            className={`mb-8 rounded-2xl border border-slate-200/70 bg-gradient-to-b ${dsAcc.band} p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-1 rounded-full ring-1 ring-inset ${dsAcc.chip}`}>
                  <DsIcon className="w-3 h-3" /> {lang === "fr" ? "Ardoise du jour" : lang === "en" ? "Today's Special" : lang === "es" ? "Especial del día" : lang === "it" ? "Piatto del giorno" : lang === "de" ? "Tagesangebot" : lang === "zh" ? "今日特色" : "本日のおすすめ"}
                </span>
              </div>
              {ds.price_ttc != null && ds.price_ttc !== "" && (
                <div className="font-mono-num text-lg font-semibold text-slate-900">{fmt(Number(ds.price_ttc))}</div>
              )}
            </div>
            <div className="mt-2 text-xl font-semibold tracking-tight text-slate-900">{dsTitle}</div>
            {dsDesc && <div className="mt-1 text-sm text-slate-600 leading-snug">{dsDesc}</div>}
            {(ds.allergens || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {ds.allergens.map((c) => (
                  <span key={c} className="text-[10px] font-mono-num font-semibold px-1.5 py-0.5 rounded bg-white/70 text-slate-700 border border-slate-200">
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Categories */}
        {Object.keys(byCategory).map((cat) => {
          return (
          <section key={cat} className="mb-10 relative" data-testid={`public-cat-${cat}`}>
            <div className="flex items-baseline gap-3 mb-4">
              <div className="w-1 h-6 rounded-full bg-slate-900" />
              <h2
                className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold"
                style={{ fontFamily: "var(--rs-menu-font-titles)" }}
                data-testid={`public-cat-title-${cat}`}
              >
                {catLabel(cat, lang)}
              </h2>
            </div>
            <div className="space-y-4">
              {byCategory[cat].map((it) => {
                const photo = showPhotos ? buildPhotoUrl(it.recipe.photo_url) : null;
                const price = it.price_override_ttc ?? it.recipe.selling_price_ttc;
                const desc = showDescriptions ? descForLang(it, lang) : "";
                return (
                  <div key={it.recipe_id} className="flex gap-4 items-start" data-testid={`public-item-${it.recipe_id}`}>
                    {photo ? (
                      <img src={photo} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-20 h-20 rounded-xl object-cover ring-1 ring-slate-200 shrink-0" data-testid={`public-photo-${it.recipe_id}`} />
                    ) : null}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[15px] font-medium text-slate-900 leading-snug">{nameForLang(it, lang)}</div>
                          {desc && (
                            <div className="mt-1 text-sm text-slate-600 leading-snug" data-testid={`public-desc-${it.recipe_id}`}>{desc}</div>
                          )}
                          {(it.recipe.allergens || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {it.recipe.allergens.map((c) => (
                                <span key={c} title={allergenLabel(c, lang)} className="text-[10px] font-mono-num font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                                  {c}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="font-mono-num text-[15px] font-semibold text-slate-900 whitespace-nowrap">{fmt(price)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
        })}

        {usedAllergens.length > 0 && (
          <div className="mt-10 pt-6 border-t border-slate-200/70">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{ALLERGENS_SECTION_LABEL[lang] || ALLERGENS_SECTION_LABEL.fr}</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600">
              {usedAllergens.map((c) => (
                <div key={c}><span className="font-mono-num font-semibold text-slate-900 mr-1">{c}</span>{allergenLabel(c, lang)}</div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-xs text-slate-500 mt-10">{h.footer}</div>
      </div>
    </div>
  );
}
